import React from "react";
import { formatCurrency, formatToLocalDate } from "../../../../helpers/utils";
import {
  DeliveryLocationQtyType,
  DeliveryLocationQtyTypeColors,
  DeliveryLocationType,
  getDeliveryPriceLabel,
  getDeliveryQtyTypeLabel,
} from "../../MasterData/DeliveryLocations";

const DeliveryDetails = ({ data }: { data: any }) => {
  const delivery = data?.delivery;
  const machineDeliveryDetails = data?.machine_service_delivery || [];
  const mezzanineDelivery = data?.mezzanine_delivery;

  const isCourier =
    delivery?.qty_type === "parcel_box" ||
    delivery?.qty_type === DeliveryLocationQtyType.PARCEL_BOX ||
    delivery?.method === "parcel_box" ||
    (typeof delivery?.name === "string" &&
      delivery.name.toLowerCase().includes("courier delivery")) ||
    (Array.isArray(delivery?.boxes) && delivery.boxes.length > 0) ||
    (Array.isArray(delivery?.parcel_boxes) && delivery.parcel_boxes.length > 0);

  const parcelBoxes: any[] =
    delivery?.boxes || delivery?.parcel_boxes || [];

  return (
    <>
      <div className="mb-4">
        <h5 className="fw-bold mb-lg-3 mb-2 fs-4 text-dark">Delivery</h5>
        <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white">
          {(delivery?.name ||
            delivery?.on_site_delivery_date ||
            delivery?.delivery_notes) && (
            <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
              <div className="row align-items-center gy-2">
                {delivery?.name && (
                  <>
                    {isCourier ? (
                      <>
                        {/* Title Row: Delivery Name on Left, Delivery Total on Right */}
                        <div className="col-md-6 col-12">
                          <h6 className="fw-bold mb-0 text-dark fs-6">
                            {delivery.name}
                          </h6>
                        </div>
                        <div className="col-md-6 col-12">
                          <div className="d-flex justify-content-md-end justify-content-between align-items-center">
                            <div
                              style={{ minWidth: "110px" }}
                              className="d-flex align-items-center gap-2"
                            >
                              <small className="text-muted fw-normal text-nowrap">
                                Total:
                              </small>
                              <span className="fw-bold text-dark text-nowrap">
                                {formatCurrency(
                                  delivery.total ?? delivery.price,
                                  data?.currency,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Multiple Rows: Each Parcel Box */}
                        {parcelBoxes.length > 0 ? (
                          parcelBoxes.map((box: any, idx: number) => {
                            const unitPrice = Number(
                              box.price ?? box.unit_price ?? 0,
                            );
                            const qty = Math.max(
                              1,
                              Number(box.quantity) || 1,
                            );
                            const lineTotal = Number(
                              box.line_total ??
                                box.total ??
                                unitPrice * qty,
                            );
                            const dimensions =
                              box.dimensions_text

                            return (
                              <React.Fragment
                                key={box.box_id || box._id || idx}
                              >
                                {/* Left Side: Parcel Box Name */}
                                <div className="col-md-6 col-12">
                                  <div
                                    className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    <span
                                      style={{
                                        color:
                                          DeliveryLocationQtyTypeColors[
                                            DeliveryLocationQtyType.PARCEL_BOX
                                          ] || "#0d6efd",
                                      }}
                                    >
                                      {box.name} {dimensions}
                                    </span>
                                  </div>
                                </div>

                                {/* Right Side: Price, Quantity & Line Total */}
                                <div className="col-md-6 col-12">
                                  <div className="d-flex justify-content-md-end justify-content-between align-items-center gap-3 flex-wrap">
                                    <div
                                      style={{ minWidth: "125px" }}
                                      className="d-flex align-items-center gap-2"
                                    >
                                      <small className="text-muted fw-normal text-nowrap">
                                        Price:
                                      </small>
                                      <span className="fw-bold text-dark text-nowrap">
                                        {formatCurrency(
                                          unitPrice,
                                          data?.currency,
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      style={{ minWidth: "60px" }}
                                      className="d-flex align-items-center gap-2"
                                    >
                                      <small className="text-muted fw-normal text-nowrap text-capitalize">
                                        Qty:
                                      </small>
                                      <span className="fw-bold text-dark">
                                        {qty}
                                      </span>
                                    </div>
                                    <div
                                      style={{ minWidth: "110px" }}
                                      className="d-flex align-items-center gap-2"
                                    >
                                      <small className="text-muted fw-normal text-nowrap">
                                        Total:
                                      </small>
                                      <span className="fw-bold text-dark text-nowrap">
                                        {formatCurrency(
                                          lineTotal,
                                          data?.currency,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <>
                            <div className="col-md-6 col-12">
                              <div
                                className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                style={{ fontSize: "0.875rem" }}
                              >
                                <span
                                  style={{
                                    color:
                                      DeliveryLocationQtyTypeColors[
                                        DeliveryLocationQtyType.PARCEL_BOX
                                      ] || "#0d6efd",
                                  }}
                                >
                                  Parcel Delivery
                                </span>
                              </div>
                            </div>
                            <div className="col-md-6 col-12">
                              <div className="d-flex justify-content-md-end justify-content-between align-items-center gap-3 flex-wrap">
                                <div
                                  style={{ minWidth: "125px" }}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <small className="text-muted fw-normal text-nowrap">
                                    Price:
                                  </small>
                                  <span className="fw-bold text-dark text-nowrap">
                                    {getDeliveryPriceLabel(
                                      delivery.price,
                                      data?.currency,
                                      data?.conversion_rate,
                                    )}
                                  </span>
                                </div>
                                <div
                                  style={{ minWidth: "60px" }}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <small className="text-muted fw-normal text-nowrap text-capitalize">
                                    Qty:
                                  </small>
                                  <span className="fw-bold text-dark">
                                    {delivery.quantity || 1}
                                  </span>
                                </div>
                                <div
                                  style={{ minWidth: "110px" }}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <small className="text-muted fw-normal text-nowrap">
                                    Total:
                                  </small>
                                  <span className="fw-bold text-dark text-nowrap">
                                    {formatCurrency(
                                      delivery.total ?? delivery.price,
                                      data?.currency,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Left Column: Name and Metadata */}
                        <div
                          className={
                            delivery.type === DeliveryLocationType.FREE
                              ? "col-12"
                              : "col-md-6 col-12"
                          }
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div className="d-flex flex-column justify-content-center">
                              <h6 className="fw-bold mb-0 text-dark fs-6">
                                {delivery.name}
                              </h6>
                              {delivery.type !== DeliveryLocationType.FREE &&
                                (delivery.qty_type ===
                                DeliveryLocationQtyType.TRAILERS ? (
                                  <div className="d-flex gap-4">
                                    <div
                                      className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                      style={{ fontSize: "0.875rem" }}
                                    >
                                      <span
                                        style={{
                                          color:
                                            DeliveryLocationQtyTypeColors[
                                              delivery.qty_type as DeliveryLocationQtyType
                                            ],
                                        }}
                                      >
                                        Half Trailer
                                      </span>
                                      <span className="mx-1">|</span>
                                      <span>
                                        {getDeliveryPriceLabel(
                                          delivery.half_trailer_price,
                                          data?.currency,
                                          data?.conversion_rate,
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                      style={{ fontSize: "0.875rem" }}
                                    >
                                      <span
                                        style={{
                                          color:
                                            DeliveryLocationQtyTypeColors[
                                              delivery.qty_type as DeliveryLocationQtyType
                                            ],
                                        }}
                                      >
                                        Full Trailer
                                      </span>
                                      <span className="mx-1">|</span>
                                      <span>
                                        {getDeliveryPriceLabel(
                                          delivery.price,
                                          data?.currency,
                                          data?.conversion_rate,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    <span
                                      style={{
                                        color:
                                          DeliveryLocationQtyTypeColors[
                                            delivery.qty_type as DeliveryLocationQtyType
                                          ],
                                      }}
                                    >
                                      {getDeliveryQtyTypeLabel(delivery.qty_type)}
                                    </span>
                                    <span className="mx-1">|</span>
                                    <span>
                                      {getDeliveryPriceLabel(
                                        delivery.price,
                                        data?.currency,
                                        data?.conversion_rate,
                                      )}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>

                        {/* Right Column: Qty and Total */}
                        {delivery.type !== DeliveryLocationType.FREE && (
                          <div className="col-md-6 col-12">
                            <div className="row justify-content-md-end justify-content-between align-items-center gx-3">
                              <div className="col-auto">
                                <div className="d-flex justify-content-end align-items-center gap-2">
                                  <small className="text-muted fw-normal text-nowrap text-capitalize">
                                    {getDeliveryQtyTypeLabel(delivery.qty_type)}{" "}
                                    Qty:
                                  </small>
                                  <span className="fw-bold text-dark">
                                    {delivery.quantity}
                                  </span>
                                </div>
                              </div>
                              <div className="col-auto" style={{ minWidth: "130px" }}>
                                <div className="d-flex justify-content-end align-items-center gap-2">
                                  <small className="text-muted fw-normal text-nowrap">
                                    Total:
                                  </small>
                                  <span className="fw-bold text-dark text-nowrap">
                                    {formatCurrency(delivery.total, data?.currency)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {delivery?.on_site_delivery_date && (
                  <div className="col-12">
                    <div className="d-flex align-items-center gap-2">
                      <small className="fs-6 fw-normal text-949494 d-block">
                        On-site Delivery Date (Not Guaranteed):
                      </small>
                      <span className="fw-medium text-dark text-nowrap">
                        {formatToLocalDate(delivery.on_site_delivery_date)}
                      </span>
                    </div>
                  </div>
                )}
                {delivery?.delivery_notes && (
                  <div className="d-flex align-items-start gap-2">
                    <small className="fs-6 fw-normal text-949494 d-block flex-shrink-0">
                      Delivery Notes:
                    </small>
                    <span className="fw-medium text-dark text-break">
                      {delivery.delivery_notes}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {!delivery?.name &&
            !delivery?.on_site_delivery_date &&
            !delivery?.delivery_notes && (
              <div className="card-body py-4 text-center text-muted">
                No delivery selected.
              </div>
            )}
        </div>
      </div>

      <div className="mb-4">
        <h5 className="fw-bold mb-lg-3 mb-2 fs-4 text-dark">
          Machine Delivery
        </h5>
        <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4">
          {machineDeliveryDetails?.length > 0 ? (
            machineDeliveryDetails?.map((item: any, index: number) => {
              return (
                <div key={item?.name || index} className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                  <div className="row gy-2 align-items-center">
                    <div className="col-md-6 col-12">
                      <div className="d-flex align-items-center gap-2">
                        <div className="d-flex flex-column justify-content-center">
                          <h6 className="fw-bold mb-0 text-dark fs-6">
                            {item?.name}
                          </h6>
                          <div
                            className="d-flex align-items-center gap-1 fw-normal text-secondary"
                            style={{ fontSize: "0.875rem" }}
                          >
                            <span>
                              {formatCurrency(item?.price, data?.currency)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-12">
                      <>
                        <div className="row justify-content-md-end justify-content-between align-items-center gx-3">
                          <div className="col-auto">
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              <small className="text-muted fw-normal text-nowrap text-capitalize">
                                {item?.quantity_name
                                  ? item?.quantity_name
                                  : "Qty"}
                                :
                              </small>
                              <span className="fw-semibold text-dark">
                                {item?.quantity ?? "-"}
                              </span>
                            </div>
                          </div>

                          <div className="col-auto">
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              <small className="text-muted fw-normal text-nowrap">
                                Total:
                              </small>
                              <span className="fw-bold text-dark text-nowrap">
                                {formatCurrency(
                                  item?.total ?? 0,
                                  data?.currency,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card-body py-4 text-center text-muted">
              No machine delivery selected.
            </div>
          )}
        </div>
      </div>

      {mezzanineDelivery && (
        <div className="mb-4">
          <h5 className="fw-bold mb-lg-3 mb-2 fs-4 text-dark">
            Mezzanine Delivery
          </h5>
          <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4">
            {(mezzanineDelivery?.name ||
              mezzanineDelivery?.on_site_delivery_date ||
              mezzanineDelivery?.delivery_notes) ? (
              <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                <div className="row align-items-center gy-2">
                  {mezzanineDelivery?.name && (
                    <>
                      {/* Left Column: Name and Metadata */}
                      <div
                        className={
                          mezzanineDelivery.type === DeliveryLocationType.FREE
                            ? "col-12"
                            : "col-md-6 col-12"
                        }
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex flex-column justify-content-center">
                            <h6 className="fw-bold mb-0 text-dark fs-6">
                              {mezzanineDelivery.name}
                            </h6>
                            {mezzanineDelivery.type !== DeliveryLocationType.FREE &&
                              (mezzanineDelivery.qty_type ===
                              DeliveryLocationQtyType.TRAILERS ? (
                                <div className="d-flex gap-4">
                                  {mezzanineDelivery.half_trailer_price !== undefined && (
                                    <div
                                      className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                      style={{ fontSize: "0.875rem" }}
                                    >
                                      <span
                                        style={{
                                          color:
                                            DeliveryLocationQtyTypeColors[
                                              mezzanineDelivery.qty_type as DeliveryLocationQtyType
                                            ],
                                        }}
                                      >
                                        Half Trailer
                                      </span>
                                      <span className="mx-1">|</span>
                                      <span>
                                        {getDeliveryPriceLabel(
                                          mezzanineDelivery.half_trailer_price,
                                          data?.currency,
                                          data?.conversion_rate,
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  <div
                                    className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    <span
                                      style={{
                                        color:
                                          DeliveryLocationQtyTypeColors[
                                            mezzanineDelivery.qty_type as DeliveryLocationQtyType
                                          ],
                                      }}
                                    >
                                      Full Trailer
                                    </span>
                                    <span className="mx-1">|</span>
                                    <span>
                                      {getDeliveryPriceLabel(
                                        mezzanineDelivery.price,
                                        data?.currency,
                                        data?.conversion_rate,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  <span
                                    style={{
                                      color:
                                        DeliveryLocationQtyTypeColors[
                                          mezzanineDelivery.qty_type as DeliveryLocationQtyType
                                        ],
                                    }}
                                  >
                                    {getDeliveryQtyTypeLabel(mezzanineDelivery.qty_type)}
                                  </span>
                                  <span className="mx-1">|</span>
                                  <span>
                                    {getDeliveryPriceLabel(
                                      mezzanineDelivery.price,
                                      data?.currency,
                                      data?.conversion_rate,
                                    )}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Qty and Total */}
                      {mezzanineDelivery.type !== DeliveryLocationType.FREE && (
                        <div className="col-md-6 col-12">
                          <div className="row justify-content-md-end justify-content-between align-items-center gx-3">
                            <div className="col-auto">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap text-capitalize">
                                  {getDeliveryQtyTypeLabel(mezzanineDelivery.qty_type)}{" "}
                                  Qty:
                                </small>
                                <span className="fw-bold text-dark">
                                  {mezzanineDelivery.quantity}
                                </span>
                              </div>
                            </div>
                            <div className="col-auto">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap">
                                  Total:
                                </small>
                                <span className="fw-bold text-dark text-nowrap">
                                  {formatCurrency(mezzanineDelivery.total, data?.currency)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {mezzanineDelivery?.on_site_delivery_date && (
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2">
                        <small className="fs-6 fw-normal text-949494 d-block">
                          On-site Delivery Date (Not Guaranteed):
                        </small>
                        <span className="fw-medium text-dark text-nowrap">
                          {formatToLocalDate(mezzanineDelivery.on_site_delivery_date)}
                        </span>
                      </div>
                    </div>
                  )}
                  {mezzanineDelivery?.delivery_notes && (
                    <div className="col-12">
                      <div className="d-flex align-items-start gap-2">
                        <small className="fs-6 fw-normal text-949494 d-block flex-shrink-0">
                          Delivery Notes:
                        </small>
                        <span className="fw-medium text-dark text-break">
                          {mezzanineDelivery.delivery_notes}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card-body py-4 text-center text-muted">
                No mezzanine delivery selected.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DeliveryDetails;
